import {
  Box,
  Button,
  Container,
  Divider,
  FormControlLabel,
  InputLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
  colors,
} from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CryptoJS from 'crypto-js';
import Cookies from 'universal-cookie';
import { toast } from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import { fetchWith401Check } from '../../helpers/http';

const UserEdit = () => {
  const [anchor, setAnchor] = React.useState(null);
  const [anchorDelete, setAnchorDelete] = React.useState(null);
  const cookies = new Cookies();
  const navigate = useNavigate();
  const params = useParams();
  const username = params.id;
  const [entries, setEntries] = React.useState({});
  const [loadingState, setLoadingState] = React.useState({ fetch: true });
  const [userdata, setUserdata] = React.useState({});
  const changePwdRef = React.useRef();
  const deleteUserRef = React.useRef();
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordState, setPasswordState] = React.useState({ new: '', confirmation: '' });

  const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;
  const APP_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_HOST : process.env.REACT_APP_HOST_PROD;

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'verificator', label: 'Reviewer' },
    { value: 'contributor', label: 'Contributor' },
  ];

  const verification_period = [
    { value: 'alltime', label: 'All time' },
    { value: '2021', label: '2021' },
    { value: '2022', label: '2022' },
    { value: '2023', label: '2023' },
  ];

  const fetchUser = () => {
    const requestOptions = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
    };

    if (!username) return toast.error('Username Not found');

    setLoadingState({ ...loadingState, fetch: true });

    fetchWith401Check(`${API_URL}/auth/user/${username}`, requestOptions)
      .then(function (response) {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then(function (response) {
        setUserdata(response.response);
      })
      .catch(function (err) {
        return toast.error('Something wrong happenned');
      })
      .finally(function () {
        setLoadingState({ ...loadingState, fetch: false });
      });
  };

  React.useEffect(() => {
    fetchUser();
  }, [username]);

  const updateEntries = () => {
    setEntries({ ...entries, username: userdata.username, fullname: userdata.name, roles: userdata.type, email: userdata.email });
  };

  React.useEffect(() => {
    updateEntries();
  }, [userdata]);

  const doChangePassword = () => {
    if (!passwordState.new) return toast.error('Please fill new password');
    if (!passwordState.confirmation) return toast.error('Please fill confirmation password');
    if (passwordState.confirmation !== passwordState.new) return toast.error('New password and old password not match');

    if (passwordState.confirmation.length < 12)
      return toast.error('Minimal password length is 12 characters with a combination of numbers, symbols, and capital letters');

    function validateText(text) {
      const hasNumber = /\d/.test(text);
      const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(text);
      const hasCapital = /[A-Z]/.test(text);

      return hasNumber && hasSymbol && hasCapital;
    }

    if (!validateText(passwordState.confirmation))
      return toast.error('Minimal password length is 12 characters with a combination of numbers, symbols, and capital letters');

    const secretKey = process.env.REACT_APP_SKEY;
    const encryptedPass = CryptoJS.AES.encrypt(passwordState.confirmation, secretKey).toString();
    const body = { username: userdata.username, password: encryptedPass };

    if (!body.username) return toast.error('No username found');
    if (!body.password) return toast.error('No password found');

    try {
      setLoadingState({ ...loadingState, update: true });

      const requestOptions = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify(body),
      };

      fetchWith401Check(`${API_URL}/auth/changepwd`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          setAnchor(null);
          toast.success('Password changed successfully');
        })
        .catch(function (err) {
          return toast.error('Something wrong happenned');
        })
        .finally(function () {
          setLoadingState({ ...loadingState, update: false });
        });
    } catch (error) {
      toast.error(error || 'Something wrong happenned');
    }
  };

  const doUpdateUser = () => {
    const data = entries;
    if (!data.username) return toast.error('Please add username');
    if (!data.fullname) return toast.error('Please add fullname');
    if (!data.email) return toast.error('Please add email');
    if (!data.roles) return toast.error('Please choose the role');

    if (data.username.length < 5) return toast.error('Minimal username length is 5 characters');
    if (data.fullname.length < 4) return toast.error('Minimal fullname length is 4 characters');

    function validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    if (!validateEmail(data.email)) return toast.error('Please use a valid email, example yourname@gmail.com');

    if (['verificator', 'editor'].includes(data.roles) && !data.verification_period) return toast.error('Please choose verification period');

    let metadata = null;
    if (['verificator', 'editor'].includes(data.roles)) {
      metadata = { verification_period: data.verification_period };
    }
    try {
      setLoadingState({ ...loadingState, update: true });
      const requestOptions = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify({
          username: data.username,
          name: data.fullname,
          type: data.roles,
          metadata,
          email: data.email,
          id: userdata.id,
        }),
      };

      fetchWith401Check(`${API_URL}/auth/updateuser`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          toast.success('User updated successfully');
        })
        .catch(function (err) {
          console.log(err);
          if (err.status === 409) return toast.error('Username conflict, please use another username.');
          return toast.error(err.message);
        })
        .finally(function () {
          setLoadingState({ ...loadingState, update: false });
        });
    } catch (error) {
      toast.error(error.message || 'Something wrong happenned');
    }
  };

  const doDeleteUser = () => {
    setLoadingState({ ...loadingState, delete: true });
    try {
      const requestOptions = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
        body: JSON.stringify({
          username: userdata.username,
        }),
      };

      fetchWith401Check(`${API_URL}/auth/deleteuser`, requestOptions)
        .then(function (response) {
          if (response.ok) {
            return response.json();
          }
          return Promise.reject(response);
        })
        .then(function (response) {
          toast.success('User deleted successfully');
          window.history.back();
        })
        .catch(function (err) {
          return toast.error(err.message);
        })
        .finally(function () {
          setLoadingState({ ...loadingState, delete: false });
        });
    } catch (error) {
      toast.error(error.message || 'Something wrong happenned');
    }
  };
  return (
    <Stack direction='column' sx={{ backgroundColor: colors.blue[100], minHeight: '100vh' }} spacing={2}>
      <Container maxWidth='sm' sx={{ backgroundColor: 'white', minHeight: '100vh', padding: 2 }}>
        <Stack direction='column' spacing={2}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Box>
              <Button onClick={() => window.history.back()} variant='outlined' startIcon={<ArrowBackIcon />}>
                Back
              </Button>
            </Box>
            <Typography
              ref={changePwdRef}
              onClick={(e) => setAnchor(e.currentTarget)}
              sx={{ fontWeight: 'bold', cursor: 'pointer', color: colors.blue[400] }}
              variant='body2'
            >
              Change Password
            </Typography>
          </Stack>
          <Divider />
          <Stack direction='column' spacing={2}>
            <Typography variant='body2'>Please fill information below to edit user</Typography>
            <Stack direction='column' spacing={2}>
              <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }} htmlFor='username'>
                  Username
                </InputLabel>
                <TextField
                  size='small'
                  margin='dense'
                  id='username'
                  type='text'
                  fullWidth
                  required
                  variant='standard'
                  value={entries.username}
                  onChange={(e) => setEntries({ ...entries, username: e.currentTarget.value.replace(/[\s\W]/g, '').toLowerCase() })}
                />
              </Stack>

              <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }} htmlFor='fullname'>
                  Fullname
                </InputLabel>
                <TextField
                  size='small'
                  margin='dense'
                  id='fullname'
                  required
                  type='text'
                  fullWidth
                  variant='standard'
                  value={entries.fullname}
                  onChange={(e) => setEntries({ ...entries, fullname: e.currentTarget.value })}
                />
              </Stack>

              <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }} htmlFor='fullname'>
                  Email address
                </InputLabel>
                <TextField
                  size='small'
                  margin='dense'
                  id='email'
                  required
                  type='text'
                  fullWidth
                  variant='standard'
                  value={entries.email}
                  onChange={(e) => setEntries({ ...entries, email: e.currentTarget.value.replace(/[\s]/g, '').toLowerCase() })}
                />
              </Stack>

              <Stack direction='column'>
                <Typography variant='body2'>Roles *</Typography>
                <RadioGroup
                  aria-labelledby='roles-group'
                  value={entries.roles}
                  name='roles-type'
                  onChange={(e) => setEntries({ ...entries, roles: e.currentTarget.value })}
                >
                  {roles.map((item, index) => (
                    <FormControlLabel
                      key={index}
                      value={item.value}
                      control={<Radio />}
                      checked={entries.roles === item.value}
                      label={
                        <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='body2'>
                          {item.label}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Stack>

              {['verificator', 'editor'].includes(entries.roles) && (
                <Stack direction='column'>
                  <Typography variant='body1'>Verification Period *</Typography>
                  <RadioGroup
                    aria-labelledby='roles-group'
                    value={entries.verification_period}
                    name='roles-type'
                    onChange={(e) => setEntries({ ...entries, verification_period: e.currentTarget.value })}
                  >
                    {verification_period.map((item, index) => (
                      <FormControlLabel
                        key={index}
                        value={item.value}
                        control={<Radio />}
                        checked={entries.verification_period === item.value}
                        label={
                          <Typography sx={{ textAlign: 'center', alignItems: 'center' }} variant='body1'>
                            {item.label}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Divider />

          <Stack direction='row' justifyContent='center'>
            <Box>
              <LoadingButton loading={loadingState.update} onClick={doUpdateUser} variant='contained'>
                {loadingState.update ? 'Updating' : 'Update User'}
              </LoadingButton>
            </Box>
          </Stack>
          <Divider />
          <Box sx={{ width: 100, alignSelf: 'flex-end' }}>
            <Typography
              ref={deleteUserRef}
              onClick={(e) => setAnchorDelete(e.currentTarget)}
              variant='body2'
              sx={{ color: colors.red[400], cursor: 'pointer', fontWeight: 'bold', textAlign: 'right' }}
            >
              Delete User
            </Typography>
          </Box>
        </Stack>
        <Popover
          id='Popup'
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Stack direction='column' sx={{ backgroundColor: colors.amber[500], minWidth: 400 }} p={1} justifyContent='center'>
            <Stack direction='column' sx={{ backgroundColor: 'white', minHeight: '100%' }} p={2} spacing={2}>
              <Stack direction='column' spacing={1}>
                <Typography variant='body2'>Please input new password and confirmation password</Typography>
              </Stack>
              <Stack direction='row' alignItems='center' sx={{ alignSelf: 'center' }}>
                <Switch onChange={() => setShowPassword(!showPassword)} checked={showPassword} />
                <Typography variant='body2'>Show Password</Typography>
              </Stack>

              {/* <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }}>Old Password</InputLabel>
                <TextField type={showPassword ? 'text' : 'password'} placeholder='Input old password'></TextField>
              </Stack> */}
              <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }}>New Password</InputLabel>
                <TextField
                  onChange={(e) => setPasswordState({ ...passwordState, new: e.currentTarget.value })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Input new password'
                ></TextField>
              </Stack>
              <Stack direction='column' spacing={1}>
                <InputLabel sx={{ fontSize: 14 }}>New Password Confirmation</InputLabel>
                <TextField
                  onChange={(e) => setPasswordState({ ...passwordState, confirmation: e.currentTarget.value })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Input new password confirmation'
                ></TextField>
              </Stack>
              <Button onClick={doChangePassword}>Change Password</Button>
            </Stack>
          </Stack>
        </Popover>
        <Popover
          id='Popup'
          open={Boolean(anchorDelete)}
          anchorEl={anchorDelete}
          onClose={() => setAnchorDelete(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'top',
          }}
        >
          <Stack direction='column' sx={{ backgroundColor: colors.amber[500], minWidth: 400 }} p={1} justifyContent='center'>
            <Stack direction='column' sx={{ backgroundColor: 'white', minHeight: '100%' }} p={2} spacing={2}>
              <Stack direction='column' spacing={1}>
                <Typography variant='body2'>Are you sure want to delete this user?</Typography>
              </Stack>
              <Stack direction='row' spacing={1}>
                <Button sx={{ color: colors.grey[600] }} onClick={() => setAnchorDelete(null)}>
                  No
                </Button>
                <Button sx={{ color: colors.red[400] }} onClick={doDeleteUser}>
                  Yes
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Popover>
      </Container>
    </Stack>
  );
};

export default UserEdit;
